import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useQueries, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Lock } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import { documentContentQueryOptions } from '#/api/document-content'
import { previewableKind, FILE_KINDS, fileKind } from '#/lib/file-kind'
import {
  filesQueryOptions,
  isAtOrPastExtracted,
  pollWhileProcessing,
} from '#/api/files'
import type { WorkspaceFile } from '#/api/files'
import { resolveDisplayStatus } from '#/lib/file-display'
import type { StatusLabel } from '#/lib/file-display'
import { workspacesQueryOptions } from '#/api/workspaces'
import type { Workspace } from '#/api/workspaces'
import { Button } from '#/components/ui/button'
import { tableLinkClass } from '#/components/ui/table'
import { DataTable } from '#/components/data-table'
import { DocumentPreview } from '#/components/documents/preview/document-preview'
import type { PreviewContent } from '#/components/documents/preview/document-preview'
import { DocumentGraphDialog } from '#/components/documents/document-graph-dialog'
import type { DocumentGraphTarget } from '#/components/documents/document-graph-dialog'
import {
  DOCUMENT_TABLE_CLASS,
  useDocumentColumns,
} from '#/components/documents/document-table'
import { EmptyState } from '#/components/empty-state'
import { FilterSelect } from '#/components/filter-select'
import { PageContainer } from '#/components/page-container'
import { PageHeader } from '#/components/page-header'
import { RouteError } from '#/components/route-error'
import { RoutePending } from '#/components/route-pending'
import { useDownloadFile } from '#/hooks/use-download-file'
import type { FileKind } from '#/lib/file-kind'
import { cn } from '#/lib/utils'
import { useCan } from '#/user/me'
import { workspaceName } from '#/lib/display-name'

export const Route = createFileRoute('/_authed/accounts/$accountId/vault')({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(
      workspacesQueryOptions(params.accountId),
    ),
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: VaultPage,
})

// A document with what the filters read off it worked out once: its coarse
// status and its kind.
type VaultRow = {
  file: WorkspaceFile
  workspace: Workspace
  status: StatusLabel
  kind: FileKind
}

// The status filter's choices, in the words the status column uses.
const STATUS_WORDS: ReadonlyArray<[StatusLabel, string]> = [
  ['processing', 'documents.statusLabels.processing'],
  ['failed', 'uploads.steps.failed'],
  ['complete', 'uploads.steps.ready'],
]

type Filters = {
  workspace: string | null
  status: StatusLabel | null
  kind: FileKind | null
}

const NO_FILTERS: Filters = { workspace: null, status: null, kind: null }

const rowId = (row: VaultRow) => `${row.workspace.uuid}:${row.file.uuid}`

// A filter's choices: those of `all` (value and label, in order) that occur
// in `held`.
function optionsHeld<T extends string>(
  all: ReadonlyArray<[T, string]>,
  held: ReadonlyArray<T>,
): Array<{ value: T; label: string }> {
  const present = new Set(held)
  return all
    .filter(([value]) => present.has(value))
    .map(([value, label]) => ({ value, label }))
}

function VaultPage() {
  const { t } = useTranslation()
  const { accountId } = Route.useParams()
  const { data: workspaces } = useSuspenseQuery(
    workspacesQueryOptions(accountId),
  )
  // Each workspace's listing is read again while any of its documents is
  // being processed, so their status cells go live as the pipeline moves.
  const fileQueries = useQueries({
    queries: workspaces.map((workspace) => ({
      ...filesQueryOptions(accountId, workspace.uuid),
      refetchInterval: pollWhileProcessing,
    })),
  })

  const isLoading = fileQueries.some((q) => q.isPending)
  const failedWorkspaces = workspaces.filter(
    (_, i) => fileQueries[i]?.isError ?? false,
  )
  // In upload order, newest first, by the table's opening sort.
  const rows: Array<VaultRow> = workspaces.flatMap((workspace, i) =>
    (fileQueries[i]?.data ?? []).map((file) => ({
      file,
      workspace,
      status: resolveDisplayStatus(file.display_status).label,
      kind: fileKind(file.original_filename, file.file_type),
    })),
  )

  const [filters, setFilters] = useState<Filters>(NO_FILTERS)
  const setFilter = <TKey extends keyof Filters>(
    key: TKey,
    value: Filters[TKey],
  ) => setFilters((prev) => ({ ...prev, [key]: value }))
  const shown = rows.filter(
    (row) =>
      (!filters.workspace || row.workspace.uuid === filters.workspace) &&
      (!filters.status || row.status === filters.status) &&
      (!filters.kind || row.kind === filters.kind),
  )
  const filtered = Object.values(filters).some((v) => v !== null)

  // Each filter offers only the values the vault holds, in its own order.
  const workspaceOptions = optionsHeld(
    workspaces.map((w): [string, string] => [w.uuid, workspaceName(t, w)]),
    rows.map((row) => row.workspace.uuid),
  )
  const statusOptions = optionsHeld(
    STATUS_WORDS.map(([s, key]): [StatusLabel, string] => [s, t(key)]),
    rows.map((row) => row.status),
  )
  const kindOptions = optionsHeld(
    FILE_KINDS.map((k): [FileKind, string] => [k, t(`vault.kinds.${k}`)]),
    rows.map((row) => row.kind),
  )

  // The graph is the internal candidate-node graph, served only to
  // `annwn:internal:read`; without it the actions column is absent.
  const canViewGraph = useCan('annwn:internal:read')
  const [graphTarget, setGraphTarget] = useState<DocumentGraphTarget | null>(
    null,
  )
  // The row being previewed, held by id so a poll's newer status reaches it.
  // Kept after closing so the sheet's content stays put while it slides away.
  const [preview, setPreview] = useState<{ id: string; open: boolean } | null>(
    null,
  )
  const previewRow = preview
    ? (rows.find((row) => rowId(row) === preview.id) ?? null)
    : null
  const openGraph = ({ file, workspace }: VaultRow) => {
    setGraphTarget({
      accountId,
      workspaceId: workspace.uuid,
      workspaceName: workspaceName(t, workspace),
      fileUploadId: file.uuid,
      fileName: file.original_filename,
    })
  }

  // The documents table's own columns, with the workspace as context. A
  // phone keeps the name, status and action; the workspace and upload join
  // from `sm`, the uploader from `lg`.
  const columns = useDocumentColumns<VaultRow>({
    fileOf: (row) => row.file,
    onOpen: (row) => setPreview({ id: rowId(row), open: true }),
    context: [
      {
        id: 'workspace',
        accessorFn: (row) => workspaceName(t, row.workspace),
        header: t('vault.columns.workspace'),
        enableSorting: false,
        meta: { className: 'hidden w-40 sm:table-cell' },
        cell: ({ row, getValue }) => (
          <Link
            to="/accounts/$accountId/workspaces/$workspaceId/documents"
            params={{ accountId, workspaceId: row.original.workspace.uuid }}
            title={getValue<string>()}
            className={cn('relative z-10 block truncate', tableLinkClass)}
          >
            {getValue<string>()}
          </Link>
        ),
      } satisfies ColumnDef<VaultRow, any>,
    ],
    graph: canViewGraph ? { onOpen: openGraph } : undefined,
  })

  // Nothing anywhere: an invitation, not an empty table.
  if (!isLoading && rows.length === 0 && failedWorkspaces.length === 0) {
    return (
      <PageContainer width="wide">
        <PageHeader title={t('vault.title')} />
        <EmptyState
          icon={Lock}
          title={t('vault.emptyTitle')}
          body={t('vault.empty')}
          action={
            <Button variant="outline" asChild>
              <Link to="/accounts/$accountId/workspaces" params={{ accountId }}>
                {t('vault.goToWorkspaces')}
              </Link>
            </Button>
          }
        />
      </PageContainer>
    )
  }

  return (
    <PageContainer width="wide">
      <PageHeader title={t('vault.title')} />

      {failedWorkspaces.length > 0 ? (
        <p className="-mt-4 text-body-sm text-muted-foreground">
          {t('vault.partialError', {
            workspaces: failedWorkspaces
              .map((w) => workspaceName(t, w))
              .join(', '),
          })}
        </p>
      ) : null}

      <DataTable
        // A new filter starts again from the first page.
        data={shown}
        columns={columns}
        loading={isLoading}
        size="dense"
        pageSize={25}
        initialSorting={[{ id: 'uploaded', desc: true }]}
        // A phone shows only name and status, so needs no minimum; from `sm`
        // the name keeps room beside the fixed columns.
        tableClassName={DOCUMENT_TABLE_CLASS}
        searchAccessor="name"
        searchPlaceholder={t('vault.searchPlaceholder')}
        getRowId={rowId}
        topRightSlot={
          <div className="flex flex-wrap items-center gap-2">
            <FilterSelect
              name={t('vault.columns.workspace')}
              allLabel={t('vault.filters.allWorkspaces')}
              value={filters.workspace}
              options={workspaceOptions}
              onChange={(v) => setFilter('workspace', v)}
            />
            <FilterSelect
              name={t('vault.filters.status')}
              allLabel={t('vault.filters.allStatuses')}
              value={filters.status}
              options={statusOptions}
              onChange={(v) => setFilter('status', v)}
            />
            <FilterSelect
              name={t('vault.filters.type')}
              allLabel={t('vault.filters.allTypes')}
              value={filters.kind}
              options={kindOptions}
              onChange={(v) => setFilter('kind', v)}
            />
          </div>
        }
        emptyMessage={
          filtered ? (
            <>
              {t('vault.noMatches')}{' '}
              <Button
                variant="link"
                className="h-auto px-0 text-body-md"
                onClick={() => setFilters(NO_FILTERS)}
              >
                {t('vault.clearFilters')}
              </Button>
            </>
          ) : (
            t('vault.empty')
          )
        }
      />

      <VaultPreview
        accountId={accountId}
        row={previewRow}
        open={preview?.open ?? false}
        onOpenChange={(open) =>
          setPreview((prev) => (prev ? { ...prev, open } : null))
        }
        onViewGraph={
          canViewGraph
            ? (row) => {
                setPreview((prev) => (prev ? { ...prev, open: false } : null))
                openGraph(row)
              }
            : undefined
        }
      />

      <DocumentGraphDialog
        target={graphTarget}
        onClose={() => setGraphTarget(null)}
      />
    </PageContainer>
  )
}

// The preview for one row: the parsed document where the browser can show it
// from the original (a PDF's pages, a CSV's rows), the original to download,
// and the graph for those who may see it.
function VaultPreview({
  accountId,
  row,
  open,
  onOpenChange,
  onViewGraph,
}: {
  accountId: string
  row: VaultRow | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onViewGraph?: (row: VaultRow) => void
}) {
  const { t, i18n } = useTranslation()
  const kind = row
    ? previewableKind(row.file.original_filename, row.file.file_type)
    : null
  const query = useQuery({
    ...documentContentQueryOptions(
      accountId,
      row?.file.uuid ?? '',
      kind ?? 'pdf',
      i18n.language,
    ),
    enabled: open && kind !== null && row?.status === 'complete',
  })
  const read = query.data
  const content: PreviewContent =
    kind === null
      ? { state: 'unavailable' }
      : query.isError
        ? { state: 'error', onRetry: () => void query.refetch() }
        : read?.kind === 'tooLarge'
          ? { state: 'tooLarge' }
          : read
            ? { state: 'ready', content: read }
            : { state: 'loading' }

  const download = useDownloadFile(accountId)

  return (
    <DocumentPreview
      open={open}
      onOpenChange={onOpenChange}
      document={
        row
          ? {
              filename: row.file.original_filename,
              mimeType: row.file.file_type,
              progress: row.file,
              workspace: (
                <Link
                  to="/accounts/$accountId/workspaces/$workspaceId/documents"
                  params={{ accountId, workspaceId: row.workspace.uuid }}
                  className={tableLinkClass}
                >
                  {workspaceName(t, row.workspace)}
                </Link>
              ),
              uploadedAt: row.file.created_at,
              uploadedBy: row.file.uploader.display_name,
            }
          : null
      }
      content={content}
      onDownload={row ? () => download.mutateAsync(row.file.uuid) : undefined}
      onViewGraph={
        row && onViewGraph && isAtOrPastExtracted(row.file.status)
          ? () => onViewGraph(row)
          : undefined
      }
    />
  )
}
